/** Site Procurement Workspace (Demo Mode only) - guided journey for a single site, per the
 * Stage 5 Procurement Readiness spec: Overview, Requirements and Approval are fully built out
 * here; Suppliers/Catalogues/Products/Ordering/Delivery Schedules/Opening Stock/Exceptions/
 * Governance/Activity are launch tiles into their existing real screens rather than
 * duplicating that functionality in a new tab.
 * lazy: launch-tile tabs are a simplification - upgrade path is to embed each area's live
 * tab content directly once the guided workspace needs richer inline editing. */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Checkbox, Chip, Grid, LinearProgress, Stack, SvgIcon, Tab, Table, TableBody, TableCell,
  TableContainer, TableRow, Tabs, Typography, useTheme,
} from '@mui/material';
import {
  AssignmentTurnedIn, Business, EventNote, Inventory2Outlined, ListAlt, LocalShippingOutlined, RuleOutlined,
  ShoppingCartOutlined, Storefront, Timeline, WarningAmberOutlined,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import { Section } from '../../../components/ui/Section';
import {
  READINESS_CHECKLIST, READINESS_STATUS_COLOR, siteChecklistProgress, siteProcurementReadiness, siteRequirements,
} from '../../../mocks/data/procurementReadinessData';

type WorkspaceTab =
  | 'overview' | 'requirements' | 'suppliers' | 'catalogues' | 'products' | 'ordering'
  | 'deliverySchedules' | 'openingStock' | 'exceptions' | 'governance' | 'approval' | 'activity';

const buildTabs = (t: (key: string, fallback: string) => string): { value: WorkspaceTab; label: string }[] => [
  { value: 'overview', label: t('tabOverview', 'Overview') },
  { value: 'requirements', label: t('tabRequirements', 'Requirements') },
  { value: 'suppliers', label: t('tabSuppliers', 'Suppliers') },
  { value: 'catalogues', label: t('tabCatalogues', 'Catalogues') },
  { value: 'products', label: t('tabProducts', 'Products') },
  { value: 'ordering', label: t('tabOrdering', 'Ordering') },
  { value: 'deliverySchedules', label: t('tabDeliverySchedules', 'Delivery Schedules') },
  { value: 'openingStock', label: t('tabOpeningStock', 'Opening Stock') },
  { value: 'exceptions', label: t('tabExceptions', 'Exceptions') },
  { value: 'governance', label: t('tabGovernance', 'Governance') },
  { value: 'approval', label: t('tabApproval', 'Approval') },
  { value: 'activity', label: t('tabActivity', 'Activity') },
];

const READINESS_ROWS = (site: typeof siteProcurementReadiness[number], t: (key: string, fallback: string) => string) => [
  { label: t('readinessSupplier', 'Supplier readiness'), value: site.supplierReadinessPct },
  { label: t('readinessCatalogue', 'Catalogue readiness'), value: site.catalogueReadinessPct },
  { label: t('readinessProduct', 'Product readiness'), value: site.productReadinessPct },
  { label: t('readinessDelivery', 'Delivery readiness'), value: site.deliveryReadinessPct },
  { label: t('readinessOpeningStock', 'Opening stock readiness'), value: site.openingStockReadinessPct },
];

const buildLaunchTiles = (
  t: (key: string, fallback: string) => string,
): Partial<Record<WorkspaceTab, { label: string; description: string; href: string; icon: JSX.Element }>> => ({
  suppliers: { label: t('launchTileSuppliersLabel', 'Open Suppliers'), description: t('launchTileSuppliersDescription', 'Review approved suppliers and onboarding status for this site.'), href: '/procurement/suppliers', icon: <Business /> },
  catalogues: { label: t('launchTileCataloguesLabel', 'Open Catalogues'), description: t('launchTileCataloguesDescription', 'Build or assign catalogues for this site.'), href: '/procurement/catalogues', icon: <ListAlt /> },
  products: { label: t('launchTileProductsLabel', 'Open Products'), description: t('launchTileProductsDescription', 'Validate product availability, pricing and pack sizes.'), href: '/procurement/products', icon: <ShoppingCartOutlined /> },
  ordering: { label: t('launchTileOrderingLabel', 'Open Ordering'), description: t('launchTileOrderingDescription', 'Configure order channels, standing orders and promotions.'), href: '/procurement/ordering', icon: <LocalShippingOutlined /> },
  deliverySchedules: { label: t('launchTileDeliverySchedulesLabel', 'Open Ordering \u2192 Delivery Schedules'), description: t('launchTileDeliverySchedulesDescription', 'Confirm delivery days, windows and lead times.'), href: '/procurement/ordering', icon: <EventNote /> },
  openingStock: { label: t('launchTileOpeningStockLabel', 'Open Ordering \u2192 Opening Stock'), description: t('launchTileOpeningStockDescription', 'Configure opening stock ahead of go-live.'), href: '/procurement/ordering', icon: <Inventory2Outlined /> },
  exceptions: { label: t('launchTileExceptionsLabel', 'Open Exceptions'), description: t('launchTileExceptionsDescription', 'Resolve supplier and catalogue exceptions blocking this site.'), href: '/procurement/exceptions', icon: <WarningAmberOutlined /> },
  governance: { label: t('launchTileGovernanceLabel', 'Open Governance'), description: t('launchTileGovernanceDescription', 'Review catalogue governance and data quality checks.'), href: '/procurement/governance', icon: <RuleOutlined /> },
  activity: { label: t('launchTileActivityLabel', 'Activity'), description: t('launchTileActivityDescription', 'Full activity history is not yet available in demo mode.'), href: '/procurement/readiness', icon: <Timeline /> },
});

const SiteProcurementWorkspace = () => {
  const { t } = useTranslation('procurementReadiness');
  const { siteCode } = useParams<{ siteCode: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('overview');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(
    () => (siteCode ? siteChecklistProgress[siteCode] ?? {} : {}),
  );

  const TABS = useMemo(() => buildTabs(t), [t]);
  const LAUNCH_TILES = useMemo(() => buildLaunchTiles(t), [t]);

  const site = useMemo(
    () => siteProcurementReadiness.find((s) => s.siteCode === siteCode),
    [siteCode],
  );
  const requirements = siteCode ? siteRequirements[siteCode] : undefined;

  const toggleChecklistItem = (id: string) => {
    setChecklist((current) => ({ ...current, [id]: !current[id] }));
  };

  const criticalOutstanding = READINESS_CHECKLIST.filter((item) => item.critical && !checklist[item.id]);
  const allComplete = READINESS_CHECKLIST.every((item) => checklist[item.id]);

  if (!site) {
    return (
      <PageArea>
        <PageTitle>{t('siteNotFound', 'Site not found')}</PageTitle>
        <Button variant="secondary"
          onClick={() => navigate('/procurement/readiness')}>{t('backToProcurementReadiness', 'Back to Procurement Readiness')}</Button>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/procurement/readiness')}
        title={t('backToProcurementReadiness', 'Back to Procurement Readiness')}
        isLink={false}
        styles={{ marginBottom: '20px' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <PageTitle subTitle={`${site.clientAccount} \u2022 ${site.siteGroup}`}>{site.siteName}</PageTitle>
        <Chip label={site.status}
          color={READINESS_STATUS_COLOR[site.status]} />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mt: 2, mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((tab) => (
          <Tab key={tab.value}
            label={tab.label}
            value={tab.value} />
        ))}
      </Tabs>

      {activeTab === 'overview' && (
        <Grid container
          spacing={3}>
          <Grid item
            xs={12}
            md={7}>
            <Section style={{ p: 4 }}>
              <Typography variant="textMD"
                sx={{ fontWeight: 600, mb: 2 }}>{t('readinessByArea', 'Readiness by area')}</Typography>
              <Stack spacing={2}>
                {READINESS_ROWS(site, t).map((row) => (
                  <Box key={row.label}>
                    <Stack direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}>
                      <Typography variant="textSM">{row.label}</Typography>
                      <Typography variant="textSM">{row.value}%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate"
                      value={row.value} />
                  </Box>
                ))}
              </Stack>
            </Section>
          </Grid>
          <Grid item
            xs={12}
            md={5}>
            <Section style={{ p: 4 }}>
              <Typography variant="textMD"
                sx={{ fontWeight: 600, mb: 2 }}>{t('statusPanelTitle', 'Status')}</Typography>
              <Stack spacing={1.5}>
                <Box><Typography variant="textXS"
                  color="text.secondary">{t('statusApprovedRangeReceived', 'Approved range received')}</Typography>
                <Typography variant="textSM">{site.approvedRangeReceived ? t('yes', 'Yes') : t('no', 'No')}</Typography></Box>
                <Box><Typography variant="textXS"
                  color="text.secondary">{t('statusOpenExceptions', 'Open exceptions')}</Typography>
                <Typography variant="textSM">{site.openExceptions}</Typography></Box>
                <Box><Typography variant="textXS"
                  color="text.secondary">{t('statusOwner', 'Owner')}</Typography>
                <Typography variant="textSM">{site.owner}</Typography></Box>
                <Box><Typography variant="textXS"
                  color="text.secondary">{t('statusLastUpdated', 'Last updated')}</Typography>
                <Typography variant="textSM">{site.lastUpdated}</Typography></Box>
                <Box><Typography variant="textXS"
                  color="text.secondary">{t('statusNextBestAction', 'Next best action')}</Typography>
                <Typography variant="textSM">{site.nextBestAction}</Typography></Box>
              </Stack>
            </Section>
            {site.blockers.length > 0 && (
              <Section style={{ marginTop: 16, p: 4 }}>
                <Typography variant="textMD"
                  sx={{ fontWeight: 600, mb: 2 }}>{t('goLiveBlockers', 'Go-live blockers')}</Typography>
                <Stack spacing={1}>
                  {site.blockers.map((blocker) => (
                    <Alert key={blocker}
                      severity="error"
                      icon={<SvgIcon fontSize="small"><WarningAmberOutlined /></SvgIcon>}>
                      {blocker}
                    </Alert>
                  ))}
                </Stack>
              </Section>
            )}
          </Grid>
        </Grid>
      )}

      {activeTab === 'requirements' && requirements && (
        <Section style={{ p: 4 }}>
          <Typography variant="textMD"
            sx={{ fontWeight: 600, mb: 2 }}>
            {t('requirementsSectionTitle', 'Inputs from Customer Onboarding, Estate and Ranges')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Object.entries(requirements).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 600, width: '35%', textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </TableCell>
                    <TableCell>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      )}

      {(['suppliers', 'catalogues', 'products', 'ordering', 'deliverySchedules', 'openingStock', 'exceptions', 'governance', 'activity'] as WorkspaceTab[])
        .includes(activeTab) && LAUNCH_TILES[activeTab] && (
        <Section style={{ p: 4 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}>
            <Box display="flex"
              alignItems="center"
              gap={2}>
              <Box sx={{
                backgroundColor: `${theme.palette.primary[500]}20`,
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary[500],
              }}>
                <SvgIcon fontSize="medium">{LAUNCH_TILES[activeTab]!.icon}</SvgIcon>
              </Box>
              <Box>
                <Typography variant="textMD"
                  sx={{ fontWeight: 600 }}>{LAUNCH_TILES[activeTab]!.label}</Typography>
                <Typography variant="textSM"
                  color="text.secondary">{LAUNCH_TILES[activeTab]!.description}</Typography>
              </Box>
            </Box>
            <Button variant="primary"
              size="lg"
              onClick={() => navigate(LAUNCH_TILES[activeTab]!.href)}>
              {t('open', 'Open')}
            </Button>
          </Box>
        </Section>
      )}

      {activeTab === 'approval' && (
        <Section style={{ p: 4 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 2 }}>
            <Typography variant="textMD"
              sx={{ fontWeight: 600 }}>{t('finalApprovalChecklist', 'Final approval checklist')}</Typography>
            <Button
              variant="primary"
              size="lg"
              disabled={criticalOutstanding.length > 0}
              startIcon={<SvgIcon><AssignmentTurnedIn /></SvgIcon>}
            >
              {t('approveProcurementReadiness', 'Approve procurement readiness')}
            </Button>
          </Box>

          {criticalOutstanding.length > 0 && (
            <Alert severity="warning"
              sx={{ mb: 2 }}>
              {t('criticalOutstanding', '{{count}} critical items outstanding - cannot mark Ready for go-live until resolved.', { count: criticalOutstanding.length })}
            </Alert>
          )}
          {allComplete && (
            <Alert severity="success"
              sx={{ mb: 2 }}>
              {t('allChecklistComplete', 'All checklist items complete - this site is ready for approval.')}
            </Alert>
          )}

          <Stack spacing={0.5}>
            {READINESS_CHECKLIST.map((item) => (
              <Stack key={item.id}
                direction="row"
                alignItems="center"
                spacing={1}>
                <Checkbox
                  checked={!!checklist[item.id]}
                  onChange={() => toggleChecklistItem(item.id)}
                />
                <Typography variant="textSM">{item.label}</Typography>
                {item.critical && (
                  <Chip label={t('blocksGoLive', 'Blocks go-live')}
                    size="small"
                    color={checklist[item.id] ? 'default' : 'error'}
                    variant={checklist[item.id] ? 'outlined' : 'filled'} />
                )}
              </Stack>
            ))}
          </Stack>
        </Section>
      )}

      {activeTab === 'activity' && !LAUNCH_TILES.activity && (
        <Section style={{ p: 4 }}>
          <Box display="flex"
            alignItems="center"
            gap={2}>
            <SvgIcon fontSize="medium"><Storefront /></SvgIcon>
            <Typography variant="textSM">{t('noActivityRecorded', 'No activity recorded yet for this site.')}</Typography>
          </Box>
        </Section>
      )}
    </PageArea>
  );
};

export default SiteProcurementWorkspace;
